import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Dna, User, Link2, CheckCircle2, Camera, Trash2, Stamp, Upload } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Badge } from "@/components/kit";
import { Avatar } from "@/components/Avatar";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [dna, setDna] = useState(null);
  const [tag, setTag] = useState("");
  const [sysco, setSysco] = useState(null);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const brandRef = useRef();
  const [brand, setBrand] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [accent, setAccent] = useState("#B8912B");
  const [align, setAlign] = useState("center");
  const [footer, setFooter] = useState("");
  const [brandUploading, setBrandUploading] = useState(false);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Profile picture updated.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };
  const removeAvatar = async () => {
    try { await api.delete("/profile/avatar"); await refreshUser(); toast.success("Profile picture removed."); }
    catch (err) { toast.error(apiErr(err)); }
  };

  useEffect(() => {
    api.get("/recipe-dna").then(({ data }) => setDna(data.dna)).catch(() => {});
    api.get("/suppliers/status").then(({ data }) => setSysco(data.sysco)).catch(() => {});
    api.get("/branding").then(({ data }) => {
      setBrand(data); setBrandName(data.brand_name || "");
      setAccent(data.brand_accent || "#B8912B"); setAlign(data.logo_align || "center");
      setFooter(data.footer_line || "");
    }).catch(() => {});
  }, []);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const onBrandLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrand((b) => ({ ...b, brand_logo_url: data.brand_logo_url }));
      toast.success("Brand logo updated. It will appear on your menu & recipe PDFs.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBrandUploading(false); if (e.target) e.target.value = ""; }
  };
  const removeBrandLogo = async () => {
    try { await api.delete("/branding/logo"); setBrand((b) => ({ ...b, brand_logo_url: null })); toast.success("Brand logo removed."); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const saveBranding = async () => {
    try {
      await api.put("/branding", { brand_name: brandName, brand_accent: accent, logo_align: align, footer_line: footer });
      toast.success("Branding saved. It will appear on your menu & recipe PDFs.");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const saveDna = async (d) => { await api.put("/recipe-dna", d); setDna(d); toast.success("Recipe DNA updated."); };
  const [editing, setEditing] = useState(false);
  const [pf, setPf] = useState(null);
  const startEdit = () => {
    setPf({ name: user?.name || "", restaurant: user?.profile?.restaurant || "", restaurant_type: user?.profile?.restaurant_type || "",
            target_food_cost: user?.profile?.target_food_cost ?? 28, average_covers: user?.profile?.average_covers ?? 0,
            cuisine_specialties: (user?.profile?.cuisine_specialties || []).join(", ") });
    setEditing(true);
  };
  const saveProfile = async () => {
    try {
      await api.post("/onboarding", { ...pf, cuisine_specialties: pf.cuisine_specialties.split(",").map((s) => s.trim()).filter(Boolean) });
      await refreshUser(); toast.success("Profile updated."); setEditing(false);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addTag = () => { if (!tag) return; const d = { ...dna, tags: [...dna.tags, tag] }; saveDna(d); setTag(""); };
  const removeTag = (t) => saveDna({ ...dna, tags: dna.tags.filter((x) => x !== t) });
  const reset = async () => { const { data } = await api.post("/recipe-dna/reset"); setDna(data.dna); toast.success("Recipe DNA reset."); };

  if (!dna) return <Loader />;
  return (
    <div>
      <PageHeader eyebrow="Preferences" title="Settings" subtitle="Your profile, Recipe DNA and supplier connections." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="matte-card p-6">
          <p className="label-eyebrow mb-4 flex items-center gap-2"><User size={14} className="text-gold" /> Chef Profile
            <button onClick={startEdit} data-testid="edit-profile-btn" className="ml-auto text-gold text-xs hover:underline normal-case tracking-normal">Edit</button>
          </p>
          <div className="flex items-center gap-4 mb-5">
            <Avatar user={user} size={72} />
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" data-testid="avatar-file-input" onChange={onAvatar} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload-btn"
                className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
                <Camera size={13} /> {uploading ? "Uploading…" : (user?.profile?.avatar_url ? "Change photo" : "Upload photo")}
              </button>
              {user?.profile?.avatar_url && (
                <button onClick={removeAvatar} data-testid="avatar-remove-btn" className="text-white/40 text-xs hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Remove</button>
              )}
            </div>
          </div>
          {editing ? (
            <div className="space-y-3">
              <ProfInput label="Name" v={pf.name} on={(x) => setPf({ ...pf, name: x })} tid="pf-name" />
              <ProfInput label="Restaurant" v={pf.restaurant} on={(x) => setPf({ ...pf, restaurant: x })} tid="pf-restaurant" />
              <ProfInput label="Restaurant type" v={pf.restaurant_type} on={(x) => setPf({ ...pf, restaurant_type: x })} tid="pf-type" />
              <div className="grid grid-cols-2 gap-3">
                <ProfInput label="Target food cost %" type="number" v={pf.target_food_cost} on={(x) => setPf({ ...pf, target_food_cost: +x })} tid="pf-fc" />
                <ProfInput label="Average covers" type="number" v={pf.average_covers} on={(x) => setPf({ ...pf, average_covers: +x })} tid="pf-covers" />
              </div>
              <ProfInput label="Cuisines (comma separated)" v={pf.cuisine_specialties} on={(x) => setPf({ ...pf, cuisine_specialties: x })} tid="pf-cuisines" />
              <div className="flex gap-2 pt-1">
                <button onClick={saveProfile} data-testid="save-profile-btn" className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest rounded-sm">Save</button>
                <button onClick={() => setEditing(false)} className="px-6 py-2.5 text-sm text-white/50 hover:text-white">Cancel</button>
              </div>
            </div>
          ) : (
          <div className="space-y-3 text-sm">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={<span className="flex items-center gap-2">{user?.email} {user?.email_verified ? <Badge tone="green">Verified</Badge> : <Badge tone="red">Unverified</Badge>}</span>} />
            <Row label="Role" value={<Badge>{user?.role?.replace("_", " ")}</Badge>} />
            <Row label="Plan" value={<Badge tone="gold">{user?.plan?.replace("_", " ")}</Badge>} />
            <Row label="Restaurant" value={user?.profile?.restaurant || "—"} />
            <Row label="Restaurant type" value={user?.profile?.restaurant_type || "—"} />
            <Row label="Target food cost" value={`${user?.profile?.target_food_cost ?? 28}%`} />
            <Row label="Cuisines" value={(user?.profile?.cuisine_specialties || []).join(", ") || "—"} />
          </div>
          )}
          {!user?.email_verified && (
            <button onClick={async () => { try { const { data } = await api.post("/auth/resend-verification"); toast.success(data.message); } catch (e) { toast.error(apiErr(e)); } }}
              data-testid="resend-verification-btn" className="btn-outline-gold mt-5 w-full py-3 text-xs uppercase tracking-widest rounded-sm">Resend Verification Email</button>
          )}
        </div>

        <div className="matte-card p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="label-eyebrow flex items-center gap-2"><Dna size={14} className="text-gold" /> Recipe DNA</p>
            <button onClick={reset} className="text-white/40 text-xs hover:text-gold">Reset</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {dna.tags.map((t) => <button key={t} onClick={() => removeTag(t)} className="px-3 py-1.5 rounded-sm text-xs border border-gold/30 bg-gold/10 text-gold hover:border-red-400/50" title="Remove">{t} ✕</button>)}
          </div>
          <div className="flex gap-2 mb-5">
            <input value={tag} onChange={(e) => setTag(e.target.value)} data-testid="dna-tag-input" placeholder="Add a preference…" onKeyDown={(e) => e.key === "Enter" && addTag()}
              className="flex-1 bg-surface border border-white/10 rounded-sm px-4 py-2.5 text-white/90 outline-none focus:border-gold/60 text-sm" />
            <button onClick={addTag} data-testid="dna-add-btn" className="btn-outline-gold px-5 text-xs uppercase tracking-widest rounded-sm">Add</button>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <span className="text-white/50 text-sm">Target cost</span>
            <input type="number" value={dna.target_cost} onChange={(e) => setDna({ ...dna, target_cost: +e.target.value })} onBlur={() => saveDna(dna)}
              className="w-20 bg-surface border border-white/10 rounded-sm px-3 py-2 text-gold font-mono outline-none focus:border-gold/60" />
          </div>
          {dna.top_ingredients?.length > 0 && <div className="mt-4"><p className="text-white/40 text-xs mb-2">Learned from your menus</p><div className="flex flex-wrap gap-2">{dna.top_ingredients.map((t) => <Badge key={t} tone="muted">{t}</Badge>)}</div></div>}
        </div>

        <div className="matte-card p-6" data-testid="branding-card">
          <p className="label-eyebrow mb-1 flex items-center gap-2"><Stamp size={14} className="text-gold" /> Kitchen Branding</p>
          <p className="text-white/45 text-sm mb-5">Add your own logo and name to brand your menu &amp; recipe PDF exports.</p>
          {brand && !brand.can_brand ? (
            <div className="rounded-sm border border-gold/20 bg-gold/5 p-4 text-sm text-white/70" data-testid="branding-locked">
              Custom branding is available on the <span className="text-gold">Executive Chef</span> and <span className="text-gold">Master Chef</span> plans.
              <a href="/pricing" className="block mt-3 btn-outline-gold px-4 py-2 text-xs uppercase tracking-widest rounded-sm text-center">Upgrade plan</a>
            </div>
          ) : (
            <div className="space-y-5">
              {brand && !brand.can_edit && (
                <div className="rounded-sm border border-white/10 bg-white/5 p-3 text-xs text-white/50" data-testid="branding-readonly">
                  Only owners and executive chefs can change kitchen branding.
                </div>
              )}
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-sm border border-white/10 bg-surface flex items-center justify-center overflow-hidden shrink-0"
                  style={brand?.brand_logo_url ? {} : {}}>
                  {brand?.brand_logo_url
                    ? <img src={`${BACKEND}${brand.brand_logo_url}`} alt="Brand logo" data-testid="brand-logo-preview" className="h-full w-full object-contain" />
                    : <Stamp size={26} className="text-white/25" />}
                </div>
                {brand?.can_edit && (
                  <div className="flex flex-col gap-2">
                    <input ref={brandRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" data-testid="brand-logo-input" onChange={onBrandLogo} />
                    <button onClick={() => brandRef.current?.click()} disabled={brandUploading} data-testid="brand-logo-upload-btn"
                      className="btn-outline-gold px-4 py-2 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
                      <Upload size={13} /> {brandUploading ? "Uploading…" : (brand?.brand_logo_url ? "Change logo" : "Upload logo")}
                    </button>
                    {brand?.brand_logo_url && (
                      <button onClick={removeBrandLogo} data-testid="brand-logo-remove-btn" className="text-white/40 text-xs hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Remove logo</button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Brand name on PDFs</label>
                <input value={brandName} onChange={(e) => setBrandName(e.target.value)} disabled={!brand?.can_edit} data-testid="brand-name-input" placeholder="e.g. Divinity Kitchen"
                  className="w-full bg-surface border border-white/10 rounded-sm px-4 py-2.5 text-white/90 outline-none focus:border-gold/60 text-sm disabled:opacity-50" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-2">Accent color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} disabled={!brand?.can_edit} data-testid="brand-accent-input"
                      className="h-10 w-12 bg-surface border border-white/10 rounded-sm cursor-pointer disabled:opacity-50" />
                    <input value={accent} onChange={(e) => setAccent(e.target.value)} disabled={!brand?.can_edit}
                      className="flex-1 bg-surface border border-white/10 rounded-sm px-3 py-2.5 text-white/90 font-mono text-sm outline-none focus:border-gold/60 disabled:opacity-50" />
                  </div>
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Logo placement</label>
                  <div className="flex gap-1.5">
                    {["left", "center", "right"].map((a) => (
                      <button key={a} onClick={() => brand?.can_edit && setAlign(a)} disabled={!brand?.can_edit} data-testid={`brand-align-${a}`}
                        className={`flex-1 py-2.5 text-xs uppercase tracking-widest rounded-sm border transition-colors disabled:opacity-50 ${align === a ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-white/50 hover:text-white"}`}>{a}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Footer line (address, phone)</label>
                <input value={footer} onChange={(e) => setFooter(e.target.value)} disabled={!brand?.can_edit} data-testid="brand-footer-input"
                  placeholder="123 Culinary Ave, Miami FL · (305) 555-0123"
                  className="w-full bg-surface border border-white/10 rounded-sm px-4 py-2.5 text-white/90 outline-none focus:border-gold/60 text-sm disabled:opacity-50" />
              </div>

              {brand?.can_edit && (
                <button onClick={saveBranding} data-testid="brand-save-btn" className="btn-gold w-full py-3 text-xs uppercase tracking-widest rounded-sm">Save Branding</button>
              )}
              <p className="text-white/30 text-xs">Your logo, name, accent color and footer appear on exported Menu &amp; Recipe PDFs.</p>
            </div>
          )}
        </div>


        <div className="matte-card p-6 lg:col-span-2">
          <p className="label-eyebrow mb-4 flex items-center gap-2"><Link2 size={14} className="text-gold" /> Supplier Connections</p>
          <div className="flex items-center justify-between p-4 rounded-sm bg-black/30 border border-white/5">
            <div><p className="text-white/90">Sysco</p><p className="text-white/40 text-sm">Flagship supplier integration · demo pricing until authorized live access</p></div>
            {sysco?.connected ? <Badge tone="green"><CheckCircle2 size={13} className="mr-1.5" /> Connected</Badge> : <Badge tone="muted">Not connected</Badge>}
          </div>
          <p className="text-white/30 text-xs mt-3">Manage connections from the Purchasing page.</p>
        </div>
      </div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between py-1"><span className="text-white/40">{label}</span><span className="text-white/85">{value}</span></div>;
}

function ProfInput({ label, type = "text", v, on, tid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full bg-surface border border-white/10 rounded-sm px-4 py-2.5 text-white/90 outline-none focus:border-gold/60 text-sm" />
    </div>
  );
}
